import { create } from 'zustand'
import api from '@/lib/api'
import { storeTokens, initTokenRefreshTimer } from '@/lib/api'
import type { PermissionMap } from '@/types'

// ── Types ──────────────────────────────────────────────────────────────────────

export interface UserProfile {
  id: string
  domain_id: string
  user_type_id: string
  parent_user_id: string | null
  name: string
  email: string | null
  phone: string | null
  timezone: string
  date_format: string
  time_format?: string
  is_active: boolean
  is_super: boolean
  is_admin: boolean
  last_login_at: string | null
  created_at: string
  updated_at: string
  /** Permission slugs from GET /users/me — e.g. "vehicles:list", "tracking:live" */
  permissions?: string[]
  /** Joined user type object (included by the API alongside user_type_id) */
  user_type?: { id: string; name: string }
}

interface AuthState {
  user: UserProfile | null
  permissions: PermissionMap
  is_super: boolean
  /** True when the logged-in user's type name is "owner" (case-insensitive).
   *  Set synchronously in loadUser() as soon as user_type is available.
   *  Use this instead of inline `user?.user_type?.name?.toLowerCase() === 'owner'`
   *  comparisons so the check is centralised in one place. */
  is_owner: boolean
  /**
   * False until loadPermissions() has completed for the first time this session.
   * While false, usePermission() grants full access so the UI never appears locked
   * during the initial bootstrap fetch.
   */
  permissionsLoaded: boolean
  isAuthenticated: boolean
  /**
   * Raw permission slugs (e.g. "reports:activity") for the current user.
   * Populated by loadPermissions(). Use this when you need slug-level granularity
   * that the collapsed PermissionMap cannot express (e.g. reports:activity vs reports:fuel).
   */
  permissionSlugs: string[]
  /**
   * Module slugs enabled for this domain (from GET /my/modules).
   * Empty while loading — super-admin and open domains always have all modules.
   * Use the `useModule(slug)` hook for component-level checks.
   */
  modules: string[]

  /** Hydrate from localStorage on app mount — call before rendering routes. */
  init: () => void
  /** Call after a successful login to store tokens and mark authenticated. */
  setTokens: (access: string, refresh: string) => void
  /** Fetch and cache the current user profile (GET /users/me). */
  loadUser: () => Promise<void>
  /** Load permissions from cache (page refresh) or API (admin users). */
  loadPermissions: () => Promise<void>
  /** Fetch enabled module slugs from GET /my/modules and store in state. */
  loadModules: () => Promise<void>
  /** Full login flow: POST /auth/login → store tokens + permissions from response */
  login: (credentials: { login: string; password: string }) => Promise<void>
  /** POST /auth/logout, then clear all auth state. */
  logout: () => Promise<void>
  /** Clear auth state without an API call (used on session expiry). */
  clearAuth: () => void
}

// ── Helpers ────────────────────────────────────────────────────────────────────

/**
 * Converts a string[] of permission slugs into a PermissionMap keyed by resource.
 * Supports both colon-separated (e.g. "vehicles:list") and dot-separated
 * (e.g. "geofences.create") formats — the API may return either.
 */
function parseSlugsToMap(slugs: string[]): PermissionMap {
  const map: PermissionMap = {}
  for (const slug of slugs) {
    if (typeof slug !== 'string') continue
    const colonIdx = slug.indexOf(':')
    const dotIdx   = slug.indexOf('.')
    // Prefer colon separator; fall back to dot
    const sepIdx   = colonIdx >= 0 ? colonIdx : dotIdx
    if (sepIdx === -1) continue
    const resource = slug.slice(0, sepIdx)
    const action   = slug.slice(sepIdx + 1)
    if (!resource) continue

    if (!map[resource]) {
      map[resource] = { canRead: false, canCreate: false, canUpdate: false, canDelete: false }
    }
    // Any assigned slug grants canRead (controls sidebar visibility)
    map[resource].canRead = true
    if (action === 'create' || action === 'assign' || action === 'assign_vehicles'
        || action === 'sync' || action === 'activate' || action === 'handover'
        || action === 'import' || action === 'swap' || action === 'optimize') {
      map[resource].canCreate = true
    } else if (action === 'update' || action === 'edit' || action === 'reset_password'
               || action === 'deactivate' || action === 'config') {
      map[resource].canUpdate = true
    } else if (action === 'delete') {
      map[resource].canDelete = true
    } else if (action === 'manage' || action.startsWith('manage_')) {
      // Handles both plain "manage" and compound "manage_subscription", "manage_entities", etc.
      map[resource].canCreate = true
      map[resource].canUpdate = true
      map[resource].canDelete = true
    } else if (action === 'stations') {
      // ev:stations → full CRUD on EV stations (replaces deprecated ev_stations:manage)
      map[resource].canCreate = true
      map[resource].canUpdate = true
      map[resource].canDelete = true
    }
    // Read-only actions — only grant canRead (already set above).
    // Explicit list prevents accidental CRUD escalation for capability-gated slugs:
    //   dashcam:view_live, dashcam:recordings, dashcam:alarm_clips
    //   ev:live, ev:history, billing:view, tracking:live, tracking:history
    //   reports:*, roster, attendance, finance, etc.
  }
  return map
}

const PERM_CACHE_KEY = 'cached_permission_slugs'

// ── 2FA Error ──────────────────────────────────────────────────────────────────

export class TwoFactorRequiredError extends Error {
  challengeToken: string
  constructor(challengeToken: string) {
    super('2FA verification required')
    this.name = 'TwoFactorRequiredError'
    this.challengeToken = challengeToken
  }
}

// ── Store ──────────────────────────────────────────────────────────────────────

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  permissions: {},
  permissionSlugs: [],
  is_super: false,
  is_owner: false,
  permissionsLoaded: false,
  isAuthenticated: false,
  modules: [],

  init: () => {
    const token = localStorage.getItem('access_token')
    if (token) {
      set({ isAuthenticated: true })
      // Restart the proactive refresh timer so page reloads don't cause logouts
      initTokenRefreshTimer()
    }

    // Listen for auth events dispatched by the Axios interceptor
    window.addEventListener('auth:session-expired', () => {
      get().clearAuth()
    })
    window.addEventListener('auth:token-refreshed', () => {
      // Token was silently refreshed — nothing extra to do here
    })
  },

  setTokens: (access, refresh) => {
    localStorage.setItem('access_token', access)
    localStorage.setItem('refresh_token', refresh)
    set({ isAuthenticated: true })
  },

  loadUser: async () => {
    const { data } = await api
      .get<{ success: true; data: UserProfile }>('/users/me')
      .then((r) => r.data)

    // Set is_owner immediately if user_type is already embedded in the profile
    const ownerFromProfile = data.user_type?.name?.toLowerCase() === 'owner'
    set({ user: data, is_owner: ownerFromProfile })

    // If the profile carries permissions, update the cache so loadPermissions()
    // can use them as a fresh fallback. Do NOT set permissionsLoaded here —
    // loadPermissions() always runs after loadUser() and is the sole authority.
    console.debug('[auth] /users/me permissions field:', data.permissions)
    if (Array.isArray(data.permissions) && data.permissions.length > 0) {
      const slugs = data.permissions as string[]
      localStorage.setItem(PERM_CACHE_KEY, JSON.stringify(slugs))
    }

    // Fetch user type name if not already included in the profile response.
    // Used to reliably detect owner-type accounts (user_type.name === 'owner')
    // regardless of what billing permissions they hold.
    if (data.user_type_id && !data.user_type) {
      try {
        const ut = await api
          .get<{ success: true; data: { id: string; name: string } }>(`/user-types/${data.user_type_id}`)
          .then((r) => r.data.data)
        if (ut?.id) {
          const isOwnerViaFetch = ut.name?.toLowerCase() === 'owner'
          set((s) => ({
            user: s.user ? { ...s.user, user_type: { id: ut.id, name: ut.name } } : null,
            is_owner: isOwnerViaFetch,
          }))
        }
      } catch {
        // Non-critical — fall back to permission-based detection
      }
    }
  },

  loadPermissions: async () => {
    const user = get().user

    const isSuperAdmin = get().is_super || (user?.is_super ?? false)
    if (isSuperAdmin) {
      set({ is_super: true, permissionsLoaded: true })
      return
    }

    if (user?.user_type_id) {
      try {
        const rawData = await api
          .get<{ success: true; data: unknown }>(`/user-types/${user.user_type_id}/permissions`)
          .then((r) => r.data.data)

        console.debug('[auth] /user-types permissions raw:', rawData)

        // Unwrap { slugs: [...] } wrapper format (new backend format)
        const normalized: unknown = (
          !Array.isArray(rawData) &&
          typeof rawData === 'object' &&
          rawData !== null &&
          Array.isArray((rawData as Record<string, unknown>).slugs)
        ) ? (rawData as { slugs: unknown[] }).slugs : rawData

        if (Array.isArray(normalized)) {
          if (normalized.length === 0) {
            // Genuinely empty permission set — clear permissions
            localStorage.setItem(PERM_CACHE_KEY, JSON.stringify([]))
            set({ permissions: {}, permissionSlugs: [], permissionsLoaded: true })
            console.debug('[auth] permissions cleared — empty slugs from API')
            return
          }
          // Re-assign rawData to the normalized array for the checks below
          ;(rawData as unknown) = normalized
        }

        if (Array.isArray(rawData)) {
          if (rawData.length === 0) {
            // API returned empty — fall through to cache rather than wiping everything
            console.debug('[auth] permissions endpoint returned [] — falling back to cache')
          } else if (typeof rawData[0] === 'string') {
            // Legacy format: string[] of slugs
            const slugs = rawData as string[]
            localStorage.setItem(PERM_CACHE_KEY, JSON.stringify(slugs))
            const map = parseSlugsToMap(slugs)
            console.debug('[auth] permissions from slug array:', map)
            set({ permissions: map, permissionSlugs: slugs, permissionsLoaded: true })
            return
          } else {
            // Object array — two sub-formats:
            //   (a) PermissionSet: has can_read / can_create / … flags
            //   (b) Permission:    plain objects with only a `slug` field
            //       (granted permissions are represented by PRESENCE in the list)
            type RawPS = { slug: string; can_read?: boolean; can_create?: boolean; can_update?: boolean; can_delete?: boolean }
            const sets = rawData as RawPS[]

            const hasCrudFlags = sets.some(
              (ps) => ps.can_read != null || ps.can_create != null ||
                      ps.can_update != null || ps.can_delete != null
            )

            if (!hasCrudFlags) {
              // Sub-format (b): plain Permission objects — the slug itself encodes
              // the action ("geofences:create"). Parse exactly like string slugs.
              const slugs = sets.filter((ps) => typeof ps.slug === 'string').map((ps) => ps.slug)
              localStorage.setItem(PERM_CACHE_KEY, JSON.stringify(slugs))
              const map = parseSlugsToMap(slugs)
              console.debug('[auth] permissions from Permission objects:', slugs, '→', map)
              set({ permissions: map, permissionSlugs: slugs, permissionsLoaded: true })
              return
            }

            // Sub-format (a): explicit CRUD flags
            const map: PermissionMap = {}
            for (const ps of sets) {
              if (!ps.can_read && !ps.can_create && !ps.can_update && !ps.can_delete) continue
              const colonIdx = ps.slug.indexOf(':')
              const dotIdx   = ps.slug.indexOf('.')
              const sepIdx   = colonIdx >= 0 ? colonIdx : dotIdx
              const resource = sepIdx >= 0 ? ps.slug.slice(0, sepIdx) : ps.slug
              if (!resource) continue
              if (!map[resource]) map[resource] = { canRead: false, canCreate: false, canUpdate: false, canDelete: false }
              if (ps.can_read)   map[resource].canRead   = true
              if (ps.can_create) map[resource].canCreate = true
              if (ps.can_update) map[resource].canUpdate = true
              if (ps.can_delete) map[resource].canDelete = true
            }
            const syntheticSlugs: string[] = []
            for (const [resource, flags] of Object.entries(map)) {
              if (flags.canRead)   syntheticSlugs.push(`${resource}:list`)
              if (flags.canCreate) syntheticSlugs.push(`${resource}:create`)
              if (flags.canUpdate) syntheticSlugs.push(`${resource}:update`)
              if (flags.canDelete) syntheticSlugs.push(`${resource}:delete`)
            }
            // Only keep slugs where at least one CRUD flag is true — revoked items
            // (all flags false) must NOT appear in permissionSlugs or they would
            // pass granular checks like permissionSlugs.includes('reports:fuel').
            const originalSlugs = sets
              .filter((ps) => ps.can_read || ps.can_create || ps.can_update || ps.can_delete)
              .map((ps) => ps.slug)
            localStorage.setItem(PERM_CACHE_KEY, JSON.stringify(originalSlugs))
            console.debug('[auth] permissions from PermissionSet objects:', map)
            set({ permissions: map, permissionSlugs: originalSlugs, permissionsLoaded: true })
            return
          }
        }
      } catch (err) {
        console.warn('[auth] /user-types permissions fetch failed:', err)
      }
    }

    // Fall back to slugs saved by loadUser() (freshest) or last login
    const cached = localStorage.getItem(PERM_CACHE_KEY)
    if (cached === '__super__') {
      set({ is_super: true, permissionsLoaded: true })
      return
    }
    if (cached) {
      try {
        const slugs = JSON.parse(cached) as string[]
        const map = parseSlugsToMap(slugs)
        console.debug('[auth] permissions from cache:', slugs, '→', map)
        set({ permissions: map, permissionSlugs: slugs, permissionsLoaded: true })
        return
      } catch {
        // Cache was corrupt
      }
    }

    console.warn('[auth] no permissions source found — setting empty')
    set({ permissions: {}, permissionSlugs: [], permissionsLoaded: true })
  },

  loadModules: async () => {
    // Super-admins always have all modules — no API call needed
    if (get().is_super) return
    try {
      const res = await api
        .get<{ success: true; data: { modules: string[] } }>('/my/modules')
        .then((r) => r.data.data)
      if (Array.isArray(res?.modules)) {
        set({ modules: res.modules })
      }
    } catch {
      // Non-critical — leave modules as [] so components fall back to permitting all
    }
  },

  login: async (credentials) => {
    interface LoginUser {
      is_super?: boolean
      permissions?: string[]
    }
    interface LoginResponse {
      success: true
      data: {
        access_token: string
        refresh_token: string
        expires_in: number
        user?: LoginUser
        requires_2fa?: boolean
        challenge_token?: string
      }
    }

    const res = await api
      .post<LoginResponse>('/auth/login', credentials)
      .then((r) => r.data)

    // 2FA required — do not store tokens yet
    if ((res.data as { requires_2fa?: boolean }).requires_2fa) {
      const challengeToken = (res.data as { challenge_token?: string }).challenge_token ?? ''
      throw new TwoFactorRequiredError(challengeToken)
    }

    // storeTokens saves both tokens AND schedules the proactive refresh timer
    storeTokens({
      access_token: res.data.access_token,
      refresh_token: res.data.refresh_token,
      expires_in: res.data.expires_in ?? 900,
    })

    // Cache login-time permissions so init() can apply them immediately on reload
    const loginUser = res.data.user
    if (loginUser?.is_super) {
      localStorage.setItem(PERM_CACHE_KEY, '__super__')
    } else if (Array.isArray(loginUser?.permissions) && loginUser.permissions.length > 0) {
      localStorage.setItem(PERM_CACHE_KEY, JSON.stringify(loginUser.permissions))
    }

    // Do NOT set isAuthenticated or trigger loadUser/loadPermissions here.
    // The caller (LoginPage) will do a full page reload via window.location.replace('/'),
    // and init() will pick up the stored tokens + cached permissions on the fresh load.
    // This avoids a double data-fetch (once here, once on reload).
  },

  logout: async () => {
    const refreshToken = localStorage.getItem('refresh_token')
    try {
      if (refreshToken) {
        await api.post('/auth/logout', { refresh_token: refreshToken })
      }
    } catch {
      // Ignore logout API errors — proceed with local cleanup regardless
    } finally {
      get().clearAuth()
    }
  },

  clearAuth: () => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user_theme_primary')
    localStorage.removeItem(PERM_CACHE_KEY)
    sessionStorage.clear()
    set({ user: null, permissions: {}, permissionSlugs: [], modules: [], is_super: false, is_owner: false, permissionsLoaded: false, isAuthenticated: false })
    // Clear ALL React Query cache so the next user doesn't see stale data
    // (vehicles, menu, settings, alerts, etc. from the previous session)
    import('@/lib/queryClient').then(({ queryClient }) => {
      queryClient.clear()
    })
  },
}))
